import { useState, useEffect } from 'react';
import { AppShell, usePersistedTab, type TabKey } from './shell/AppShell';
import { HomeScreen } from './screens/HomeScreen';
import { ClausesScreen, type ClausesView } from './screens/ClausesScreen';
import { ContractSummary } from './components/ContractSummary';
import { Obligation } from './components/Obligation';
import { ClauseCollaboration } from './components/ClauseCollaboration';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { resolveContractContext } from './utils/contractResolver';

// Legacy view type kept for backwards-compat with old ?view=... URLs
// embedded in cached manifests.
type LegacyView =
  | 'dashboard'
  | 'home'
  | 'clause-library'
  | 'clause-suggestion'
  | 'obligation'
  | 'collaboration'
  | 'risk-analysis'
  | 'contract-summary'
  | 'redline-summary'
  | 'chatbot'
  | 'playbook';

// Map a legacy view (from ?view=... URL params or older localStorage)
// to a {tab, clausesView} pair in the current 5-tab shell.
function legacyToTab(view: LegacyView): { tab: TabKey; clausesView: ClausesView | null } {
  switch (view) {
    case 'clause-library':
      return { tab: 'clauses', clausesView: 'library' };
    case 'clause-suggestion':
      return { tab: 'clauses', clausesView: 'suggestion' };
    case 'contract-summary':
      return { tab: 'summary', clausesView: null };
    case 'obligation':
      return { tab: 'obligations', clausesView: null };
    case 'collaboration':
      return { tab: 'collaboration', clausesView: null };
    // Risk Analysis / Chatbot / Playbook are now opened from Home Quick
    // Actions instead of dedicated tabs; the legacy mapper just lands on
    // Home and the user clicks the relevant Quick Action.
    case 'risk-analysis':
    case 'chatbot':
    case 'playbook':
    case 'redline-summary':
      return { tab: 'home', clausesView: null };
    default:
      return { tab: 'home', clausesView: null };
  }
}

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

function App() {
  const [tab, setTab] = usePersistedTab('home');
  const [clausesView, setClausesView] = useState<ClausesView>('library');
  const [isOfficeInitialized, setIsOfficeInitialized] = useState(false);
  const [contractMeta, setContractMeta] = useState<{
    title: string | null;
    status: string | null;
    counterparty: string | null;
  }>({ title: null, status: null, counterparty: null });
  const [reviewBadge, setReviewBadge] = useState<number | undefined>(undefined);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // Whether a contract context could be resolved. When it can't (the add-in
  // was opened standalone in Word — desktop or web — rather than from an
  // Ezylex CLM contract), we show the WelcomeScreen instead of the feature
  // screens, which would otherwise all show "contract context not available"
  // errors. 'loading' keeps the spinner until resolution settles.
  const [contextState, setContextState] =
    useState<'loading' | 'connected' | 'welcome'>('loading');

  // Resolve the open contract once so the header can show its title.
  useEffect(() => {
    (async () => {
      try {
        const ctx = await resolveContractContext();
        // A resolvable contractId is the signal that we were opened from
        // Ezylex CLM. Without it, none of the features can function, so we
        // land on the welcome screen. (tenantId alone isn't enough — the
        // anonymous add-in userdata always carries a tenantId.)
        if (!ctx?.contractId) {
          setContextState('welcome');
          return;
        }
        setContextState('connected');
        setContractMeta({
          title: ctx.contractTitle || ctx.templateTypeName || null,
          status: null,
          counterparty: null,
        });
        // Fetch a pending-redline count for the bottom-nav badge.
        // Contract-scoped so the badge can't leak counts from another
        // contract in the same tenant. Best-effort, no spinner.
        if (ctx.tenantId) {
          try {
            const tok = import.meta.env.VITE_ADDIN_API_TOKEN || '';
            const base = import.meta.env.VITE_API_BASE_URL || '';
            const url = ctx.contractId
              ? `${base}/api/contracts/${ctx.contractId}/pending-redlines`
              : `${base}/api/addin/latest-pending-playbook-run?tenantId=${encodeURIComponent(ctx.tenantId)}`;
            const res = await fetch(url, {
              headers: { 'x-addin-token': tok, 'ngrok-skip-browser-warning': 'true' },
            });
            if (res.ok) {
              const data = await res.json();
              const pending = (data?.pending || []).filter(
                (p: any) => !p.redline_applied
              ).length;
              setReviewBadge(pending > 0 ? pending : undefined);
            } else if (res.status === 404 || res.status === 204) {
              setReviewBadge(undefined);
            }
          } catch {}
        }
      } catch {
        // Resolution failed outright — treat as standalone/not connected.
        setContextState('welcome');
      }
    })();
  }, []);

  // Poll dirty state so the header shows "Unsaved changes" the moment
  // anything (typing, playbook apply, obligation add, etc.) modifies the
  // WOPI-hosted file. Same 3s cadence as the CLM /edit page.
  //
  // ALSO register a Word.js event listener for content changes. Word
  // Online buffers WOPI writes so the server-side mtime lags behind
  // real edits by 30-60s; the Word.js signal lets us POST a dirty flag
  // to the server the instant the user starts editing.
  useEffect(() => {
    let cancelled = false;
    let t: ReturnType<typeof setInterval> | null = null;
    let markedDirty = false;
    (async () => {
      const ctx = await resolveContractContext();
      if (!ctx?.contractId || cancelled) return;
      const tok = import.meta.env.VITE_ADDIN_API_TOKEN || '';
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${base}/api/contracts/${ctx.contractId}/dirty`;
      const headers = {
        'x-addin-token': tok,
        'ngrok-skip-browser-warning': 'true',
      } as Record<string, string>;

      const check = async () => {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok || cancelled) return;
          const data = await res.json();
          if (typeof data?.isDirty === 'boolean') setIsDirty(data.isDirty);
        } catch {}
      };
      check();
      t = setInterval(check, 3000);

      // Tell the server "the user just edited" so both the CLM /edit
      // page and this add-in stop guessing based on WOPI mtimes.
      const markDirtyOnce = async () => {
        if (markedDirty) return;
        markedDirty = true;
        try {
          await fetch(url, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: '{}',
          });
          // Immediate optimistic UI update — don't wait for the next poll.
          if (!cancelled) setIsDirty(true);
        } catch {
          markedDirty = false; // let the next edit event try again
        }
      };

      try {
        // Fire on ANY selection movement in Word — cheapest reliable
        // "user is interacting with the doc" signal we can grab from
        // Office.js. In practice the user has to click into the doc or
        // move the cursor before typing, so this triggers on the first
        // interaction.
        if (
          typeof Office !== 'undefined' &&
          Office?.context?.document?.addHandlerAsync
        ) {
          Office.context.document.addHandlerAsync(
            Office.EventType.DocumentSelectionChanged,
            markDirtyOnce
          );
        }
      } catch (err) {
        console.warn('[dirty] could not register Word listener', err);
      }
    })();
    return () => {
      cancelled = true;
      if (t) clearInterval(t);
    };
  }, []);

  // Listen for Home auto-run / Re-run completion events so the badge
  // refreshes without a page reload.
  useEffect(() => {
    const onRedlinesChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.openRedlines === 'number') {
        setReviewBadge(detail.openRedlines > 0 ? detail.openRedlines : undefined);
      }
    };
    window.addEventListener('ezylex:redlines-updated', onRedlinesChange as EventListener);
    return () =>
      window.removeEventListener('ezylex:redlines-updated', onRedlinesChange as EventListener);
  }, []);

  // Fetch and store user data in localStorage (similar to the prior CLM)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Check if userdata already exists in localStorage WITH tenantId
        const existingUserData = localStorage.getItem('userdata');
        if (existingUserData) {
          try {
            const parsed = JSON.parse(existingUserData);
            if (parsed?.tenantId) {
              console.log('User data already in localStorage with tenantId:', parsed.tenantId);
              return;
            }
            // tenantId missing — refetch
            console.log('User data in localStorage but missing tenantId, refetching...');
          } catch {
            // invalid JSON — refetch
          }
        }

        // Fetch user data from API
        const response = await fetch(`${API_BASE_URL}/api/addin/userdata`, {
          headers: {
            'x-addin-token': import.meta.env.VITE_ADDIN_API_TOKEN || '',
            'ngrok-skip-browser-warning': 'true',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.userdata) {
            // Store user data in localStorage (similar to the prior CLM)
            localStorage.setItem('userdata', JSON.stringify(data.userdata));
            console.log('User data stored in localStorage:', data.userdata);
          }
        } else {
          console.warn('Failed to fetch user data:', response.status);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        // Don't show error to user, just log it
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    // One-shot legacy navigation check: if the URL has ?view=...
    // (or a legacy localStorage/sessionStorage/hash hint), apply it ONCE
    // and then clear the source so it doesn't override the user's tab
    // choices later. The old code polled every 500ms which fought any
    // user-driven tab switch.
    const runInitialNavCheck = () => {
      const validViews = ['dashboard', 'clause-library', 'clause-suggestion', 'obligation', 'collaboration', 'risk-analysis', 'contract-summary', 'redline-summary', 'chatbot', 'playbook'];

      const apply = (legacy: LegacyView) => {
        const { tab: t, clausesView: cv } = legacyToTab(legacy);
        setTab(t);
        if (cv) setClausesView(cv);
      };

      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const viewParam = urlParams.get('view') as LegacyView | null;
        if (viewParam && validViews.includes(viewParam)) {
          apply(viewParam);
          // Strip ?view= from the URL so the next render / visibility
          // change doesn't re-apply it.
          try {
            urlParams.delete('view');
            const newSearch = urlParams.toString();
            const newUrl =
              window.location.pathname +
              (newSearch ? `?${newSearch}` : '') +
              window.location.hash;
            window.history.replaceState(null, '', newUrl);
          } catch {}
          return;
        }
      }

      const storedView = localStorage.getItem('ezylex-view') as LegacyView | null;
      if (storedView && validViews.includes(storedView)) {
        apply(storedView);
        localStorage.removeItem('ezylex-view');
        return;
      }

      const sessionView = sessionStorage.getItem('ezylex-view') as LegacyView | null;
      if (sessionView && validViews.includes(sessionView)) {
        apply(sessionView);
        sessionStorage.removeItem('ezylex-view');
        return;
      }

      if (typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (hash) {
          const viewFromHash = hash.replace('#', '') as LegacyView;
          if (validViews.includes(viewFromHash)) {
            apply(viewFromHash);
            window.location.hash = '';
          }
        }
      }
    };

    if (typeof Office !== 'undefined') {
      Office.onReady(() => {
        setIsOfficeInitialized(true);
        runInitialNavCheck();
      });
    } else {
      setIsOfficeInitialized(true);
      runInitialNavCheck();
    }
  }, []);

  const [homeChatFull, setHomeChatFull] = useState(false);

  const handleHomeNavigate = (next: TabKey, view?: string) => {
    setTab(next);
    if (next === 'clauses' && (view === 'library' || view === 'suggestion')) {
      setClausesView(view);
    }
  };

  let body;
  // The Home → Assistant (chat) view renders full-height and borderless, so it
  // needs the flush shell layout (no padding, full-height flex column).
  const flush = tab === 'home' && homeChatFull;
  if (tab === 'home') {
    body = (
      <HomeScreen onNavigate={handleHomeNavigate} onAssistantOpenChange={setHomeChatFull} />
    );
  } else if (tab === 'summary') {
    body = <ContractSummary />;
  } else if (tab === 'obligations') {
    body = <Obligation />;
  } else if (tab === 'collaboration') {
    body = <ClauseCollaboration />;
  } else {
    body = <ClausesScreen initialView={clausesView} />;
  }

  if (!isOfficeInitialized || contextState === 'loading') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg-soft)' }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3"
            style={{ borderColor: 'var(--brand-3)' }}
          ></div>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            Loading Ezylex AI CLM…
          </p>
        </div>
      </div>
    );
  }

  // Opened standalone (no Ezylex CLM contract in context) — show the welcome
  // landing instead of feature screens that can't function without a contract.
  if (contextState === 'welcome') {
    return <WelcomeScreen />;
  }

  return (
    <AppShell
      activeTab={tab}
      onTabChange={(t) => setTab(t)}
      contractTitle={contractMeta.title}
      contractStatus={contractMeta.status}
      counterparty={contractMeta.counterparty}
      reviewBadge={reviewBadge}
      isDirty={isDirty}
      flush={flush}
    >
      {body}
    </AppShell>
  );
}


export default App;
